import type { IAnalytics } from "@/application/ports/IAnalytics";
import type { ILogger } from "@/application/ports/ILogger";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { INavigationService } from "@/application/ports/INavigationService";
import type {
	IAuthenticationService,
	IStorageService,
	ITokenService,
	IUserStorageService,
} from "@/application/ports/services";
import { EventNotifier } from "@/application/services/EventNotifier";
import { TokenManager } from "@/application/services/TokenManager";
import {
	ForgotPasswordUseCase,
	LoginUseCase,
	LoginWithTokensUseCase,
	LogoutUseCase,
	ResetPasswordUseCase,
	SwitchOrganizationUseCase,
	ValidateAuthStatusUseCase,
} from "@/application/use-cases";
import { AuthenticationService } from "@/infrastructure/auth/AuthenticationService";
import { getOrganizationRepository } from "./repositories";
import { logger } from "@/infrastructure/logger";
import {
	forceNavigate,
	openExternalUrl,
	reloadPage,
	getPageOrigin,
} from "@/infrastructure/navigation";
import { trackUserFacingError } from "@/infrastructure/analytics";
import {
	captureException,
	captureMessage,
	clearUserContext,
	setUserContext,
} from "@/infrastructure/monitoring";
import { FileDownloadService } from "@/infrastructure/services/FileDownloadService";
import { AiAgentSessionService } from "@/infrastructure/storage/AiAgentSessionService";
import { AppSettingsStorageService } from "@/infrastructure/storage/AppSettingsStorageService";
import { HubProfileStorageService } from "@/infrastructure/storage/HubProfileStorageService";
import { LocalStorageService } from "@/infrastructure/storage/LocalStorageService";
import { OrgStorageService } from "@/infrastructure/storage/OrgStorageService";
import { PaginationStorageService } from "@/infrastructure/storage/PaginationStorageService";
import { SessionStorageService } from "@/infrastructure/storage/SessionStorageService";
import { TokenService } from "@/infrastructure/storage/TokenService";
import { UserStorageService } from "@/infrastructure/storage/UserStorageService";

// ── Core ───────────────────────────────────────────────────────────────────────

let analyticsInstance: IAnalytics;
let loggerInstance: ILogger;
let monitoringInstance: IMonitoring;

export function getAnalytics(): IAnalytics {
	if (!analyticsInstance)
		analyticsInstance = {
			trackUserFacingError,
		};
	return analyticsInstance;
}

export function getLogger(): ILogger {
	if (!loggerInstance) loggerInstance = logger;
	return loggerInstance;
}

export function getMonitoring(): IMonitoring {
	if (!monitoringInstance)
		monitoringInstance = {
			captureException,
			captureMessage,
			setUserContext,
			clearUserContext,
		};
	return monitoringInstance;
}

// ── Storage ────────────────────────────────────────────────────────────────────

let localStorageServiceInstance: LocalStorageService;
let sessionStorageServiceInstance: SessionStorageService;
let tokenServiceInstance: TokenService;
let userStorageServiceInstance: UserStorageService;
let hubProfileStorageInstance: HubProfileStorageService;
let appSettingsStorageInstance: AppSettingsStorageService;
let paginationStorageInstance: PaginationStorageService;
let orgStorageInstance: OrgStorageService;
let aiAgentSessionInstance: AiAgentSessionService;

export function getLocalStorageService(): IStorageService {
	if (!localStorageServiceInstance) {
		localStorageServiceInstance = new LocalStorageService();
	}
	return localStorageServiceInstance;
}

export function getSessionStorageService(): IStorageService {
	if (!sessionStorageServiceInstance) {
		sessionStorageServiceInstance = new SessionStorageService();
	}
	return sessionStorageServiceInstance;
}

export function getTokenService(): ITokenService {
	if (!tokenServiceInstance) {
		tokenServiceInstance = new TokenService(getLocalStorageService());
	}
	return tokenServiceInstance;
}

export function getUserStorageService(): IUserStorageService {
	if (!userStorageServiceInstance) {
		userStorageServiceInstance = new UserStorageService(
			getLocalStorageService(),
		);
	}
	return userStorageServiceInstance;
}

export function getHubProfileStorageService(): HubProfileStorageService {
	if (!hubProfileStorageInstance) {
		hubProfileStorageInstance = new HubProfileStorageService(
			getLocalStorageService(),
		);
	}
	return hubProfileStorageInstance;
}

export function getAppSettingsStorageService(): AppSettingsStorageService {
	if (!appSettingsStorageInstance) {
		appSettingsStorageInstance = new AppSettingsStorageService(
			getLocalStorageService(),
		);
	}
	return appSettingsStorageInstance;
}

export function getPaginationStorageService(): PaginationStorageService {
	if (!paginationStorageInstance) {
		paginationStorageInstance = new PaginationStorageService(
			getLocalStorageService(),
		);
	}
	return paginationStorageInstance;
}

export function getOrgStorageService(): OrgStorageService {
	if (!orgStorageInstance) {
		orgStorageInstance = new OrgStorageService(getLocalStorageService());
	}
	return orgStorageInstance;
}

export function getAiAgentSessionService(): AiAgentSessionService {
	if (!aiAgentSessionInstance) {
		aiAgentSessionInstance = new AiAgentSessionService(
			getSessionStorageService(),
		);
	}
	return aiAgentSessionInstance;
}

// ── File download ─────────────────────────────────────────────────────────────

let fileDownloadInstance: FileDownloadService;

export function getFileDownloadService(): FileDownloadService {
	if (!fileDownloadInstance) fileDownloadInstance = new FileDownloadService();
	return fileDownloadInstance;
}

// ── Auth services ──────────────────────────────────────────────────────────────

let authServiceInstance: IAuthenticationService;
let tokenManagerInstance: TokenManager;

export function getAuthService(): IAuthenticationService {
	if (!authServiceInstance) {
		authServiceInstance = new AuthenticationService();
	}
	return authServiceInstance;
}

export function getTokenManager(): TokenManager {
	if (!tokenManagerInstance) {
		tokenManagerInstance = new TokenManager(
			getTokenService(),
			getAuthService(),
			getLogger(),
		);
	}
	return tokenManagerInstance;
}

// ── Use cases ──────────────────────────────────────────────────────────────────

let loginUseCaseInstance: LoginUseCase;
let logoutUseCaseInstance: LogoutUseCase;
let validateAuthUseCaseInstance: ValidateAuthStatusUseCase;
let loginWithTokensUseCaseInstance: LoginWithTokensUseCase;
let forgotPasswordUseCaseInstance: ForgotPasswordUseCase;
let resetPasswordUseCaseInstance: ResetPasswordUseCase;
let switchOrgUseCaseInstance: SwitchOrganizationUseCase;

export function getLoginUseCase(): LoginUseCase {
	if (!loginUseCaseInstance) {
		loginUseCaseInstance = new LoginUseCase(
			getAuthService(),
			getTokenService(),
			getUserStorageService(),
			getMonitoring(),
		);
	}
	return loginUseCaseInstance;
}

export function getLogoutUseCase(): LogoutUseCase {
	if (!logoutUseCaseInstance) {
		logoutUseCaseInstance = new LogoutUseCase(
			getAuthService(),
			getTokenService(),
			getUserStorageService(),
			getLogger(),
			getMonitoring(),
		);
	}
	return logoutUseCaseInstance;
}

export function getValidateAuthUseCase(): ValidateAuthStatusUseCase {
	if (!validateAuthUseCaseInstance) {
		validateAuthUseCaseInstance = new ValidateAuthStatusUseCase(
			getAuthService(),
			getTokenService(),
			getUserStorageService(),
			getLogger(),
			getMonitoring(),
		);
	}
	return validateAuthUseCaseInstance;
}

export function getLoginWithTokensUseCase(): LoginWithTokensUseCase {
	if (!loginWithTokensUseCaseInstance) {
		loginWithTokensUseCaseInstance = new LoginWithTokensUseCase(
			getTokenService(),
			getUserStorageService(),
			getMonitoring(),
		);
	}
	return loginWithTokensUseCaseInstance;
}

export function getForgotPasswordUseCase(): ForgotPasswordUseCase {
	if (!forgotPasswordUseCaseInstance) {
		forgotPasswordUseCaseInstance = new ForgotPasswordUseCase(getAuthService());
	}
	return forgotPasswordUseCaseInstance;
}

export function getResetPasswordUseCase(): ResetPasswordUseCase {
	if (!resetPasswordUseCaseInstance) {
		resetPasswordUseCaseInstance = new ResetPasswordUseCase(getAuthService());
	}
	return resetPasswordUseCaseInstance;
}

export function getSwitchOrganizationUseCase(): SwitchOrganizationUseCase {
	if (!switchOrgUseCaseInstance) {
		switchOrgUseCaseInstance = new SwitchOrganizationUseCase(
			getOrganizationRepository(),
			getTokenService(),
			getOrgStorageService(),
		);
	}
	return switchOrgUseCaseInstance;
}

// ── Event notifiers ────────────────────────────────────────────────────────────

let sessionExpiredNotifierInstance: EventNotifier<[message?: string]>;
let loginRedirectNotifierInstance: EventNotifier;

export function getSessionExpiredNotifier(): EventNotifier<[message?: string]> {
	if (!sessionExpiredNotifierInstance) {
		sessionExpiredNotifierInstance = new EventNotifier();
	}
	return sessionExpiredNotifierInstance;
}

export function getLoginRedirectNotifier(): EventNotifier {
	if (!loginRedirectNotifierInstance) {
		loginRedirectNotifierInstance = new EventNotifier();
	}
	return loginRedirectNotifierInstance;
}

// ── Navigation ────────────────────────────────────────────────────────────────

let navigationServiceInstance: INavigationService;

export function getNavigationService(): INavigationService {
	if (!navigationServiceInstance) {
		navigationServiceInstance = {
			forceNavigate,
			openExternalUrl,
			reloadPage,
			getPageOrigin,
		};
	}
	return navigationServiceInstance;
}
